import axios from 'axios';
import * as types from '../mutation-types';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {ESignerFieldData, ESignTool} from '@/types/esign';
import type {DocumentType, TemplateData} from '@/store/modules/document';
import {d} from '@/utils/consoleHelper.ts';
import {esignTools} from '@/constants/esignTools';


export interface TemplateInterface {
    data: null;
    documents: DocumentType;
    file_id: null;
    name: null;
    pages: number;
    version: 'v1' | 'v2';
    uuid?: string;
    updated_at?: string;
    created_at?: string;
}

export interface documentImagesInterface {
    path: string;
    position: number;
}

export enum TemplateLoadingSubject {
    Template = 'template',
}

export interface TemplateState {
    init: boolean;
    loading: Record<TemplateLoadingSubject, boolean>,
    canvasActive: boolean;
    toolsActive: boolean;
    documents: DocumentType[];
    document: DocumentType | null;
    templates: TemplateData[];

    template: TemplateInterface | null;

    currentWorkingTool: ESignTool | null;
    selectedFieldV2: ESignerFieldData | null;
    selectedFieldIndex: number | null;

    documentFieldData: any[];

    allTools: ESignTool[];
    currentSigner: string;
    signerItems: any[];

    documentImages: documentImagesInterface[];
}


const state: TemplateState = {
    init: false,
    loading: {
        template: false,
    },
    canvasActive: false,
    toolsActive: false,
    documents: [],
    document: null,
    templates: [],
    template: null,

    currentWorkingTool: null,
    selectedFieldV2: null,
    selectedFieldIndex: null,

    documentFieldData: [],

    allTools: esignTools,
    currentSigner: 'primary',
    signerItems: [
        {
            title: 'Primary',
            value: 'primary',
            color: '#5be8ff',
        },
        {
            title: 'Secondary',
            value: 'secondary',
            color: 'purple',
        },
    ],

    documentImages: [],
};

const getters = {
    init: (state: TemplateState) => state.init,
    toolsActive: (state: TemplateState) => state.toolsActive,
    loading: (state: TemplateState) => state.loading,
    document: (state: TemplateState) => state.document,
    documents: (state: TemplateState) => state.documents,
    templates: (state: TemplateState) => state.templates,
    template: (state: TemplateState) => state.template,
    currentWorkingTool: (state: TemplateState) => state.currentWorkingTool,
    selectedFieldV2: (state: TemplateState) => state.selectedFieldV2,
    selectedFieldIndex: (state: TemplateState) => state.selectedFieldIndex,
    documentFieldData: (state: TemplateState) => state.documentFieldData,
    allTools: (state: TemplateState) => state.allTools,
    currentSigner: (state: TemplateState) => state.currentSigner,
    signerItems: (state: TemplateState) => state.signerItems,
    documentImages: (state: TemplateState) => state.documentImages,
};

const mutations = {
    [types.SET_TEMPLATES_LOADING](state: TemplateState, payload: {
        subject: TemplateLoadingSubject;
        loading: boolean
    }) {
        state.loading[payload.subject] = payload.loading;
    },
    [types.SET_FIELD_CURRENT_SIGNER](state: TemplateState, signer: string) {
        state.currentSigner = signer;
    },
    [types.SET_TOOLS_ACTIVE](state: TemplateState, active: boolean) {
        state.toolsActive = active;
    },
    [types.SET_CANVAS_ACTIVE](state: TemplateState, active: boolean) {
        state.canvasActive = active;
    },
    [types.SET_CURRENT_WORKING_TOOL](state: TemplateState, tool: ESignTool) {
        state.currentWorkingTool = tool;
    },
    [types.SET_SELECTED_FIELD_V2](state: TemplateState, field: ESignerFieldData) {
        d.dir('SET_SELECTED_FIELD_V2');
        d.dir(field);
        state.selectedFieldV2 = field;
    },
    [types.REMOVE_FIELD](state: TemplateState, field: ESignerFieldData) {
        const documents = state.template?.documents;
        if (documents && documents.data) {
            documents.data = documents.data.filter((f: ESignerFieldData) => f.id !== field.id);
        }
    },
    [types.REMOVE_ALL_FIELDS](state: TemplateState) {
        const documents = state.template?.documents;
        if (documents && documents.data) {
            documents.data = [];
        }
    },
    [types.SET_SELECTED_FIELD_INDEX_V2](state: TemplateState, index: number) {
        state.selectedFieldIndex = index;
    },

    [types.UPDATE_DOCUMENT_TEMPLATE_DATA](state: TemplateState, payload: { data: ESignerFieldData, index: number }) {
        if (!state.template) return;

        if (payload.data === null) {
            state.template.documents.data.splice(payload.index, 1); // Remove element safely
        } else {
            state.template.documents.data[payload.index] = payload.data;
        }
    },

    [types.ADD_TO_DOCUMENT_TEMPLATE_DATA](state: TemplateState, payload: ESignerFieldData) {
        if (!state.template) return;

        state.template.documents.data.push(payload);
    },
    [types.SET_ONE_TEMPLATE](state: TemplateState, template: TemplateInterface) {
        if (template?.documents?.data === null) {
            template.documents.data = [];
        }

        state.template = template;

    },
    [types.RESET_TEMPLATE_DATA](state: TemplateState) {
        state.template = null;
    },
    [types.SET_DOCUMENT_IMAGES](state: TemplateState, data: documentImagesInterface[]) {
        state.documentImages = data;
    },
};

const actions = {
    async getTemplate({commit}, uuid: string) {
        const responseData = await performApiRequest(commit,
            'SET_TEMPLATES_LOADING',
            TemplateLoadingSubject.Template,
            () => axios.get(`/v1/templates/${uuid}`)
        );
        commit(types.SET_ONE_TEMPLATE, responseData.template);
    },

    deleteSelectedElementsV2({state, commit}) {
        if (state.selectedFieldIndex >= 0) {
            state.template?.documents?.data.splice(state.selectedFieldIndex, 1);
            commit(types.SET_SELECTED_FIELD_V2, null);
            commit(types.SET_SELECTED_FIELD_INDEX_V2, null);
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
